import { Injectable } from '@angular/core';
import { formatDateToISO } from '../utils';

@Injectable({ providedIn: 'root' })
export class DateService {
  static convertTimeStampToISO(dateTimestamp: string, endDate?: boolean) {
    const date = new Date(dateTimestamp);
    if (endDate) this.setToEndOfDay(date);
    else this.setToStartOfDay(date);

    const isoString = date.toISOString();
    return isoString;
  }

  static setToEndOfDay(date: Date): Date {
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    date.setMilliseconds(999);
    return date;
  }

  static setToStartOfDay(date: Date): Date {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  static formatDateToDDMMYYYY(date: Date): string {
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
  }

  static formatDateToUTC(date: Date): string {
    const utcDate = new Date(date.toUTCString());
    const day = ('0' + utcDate.getUTCDate()).slice(-2);
    const month = ('0' + (utcDate.getUTCMonth() + 1)).slice(-2);
    const year = utcDate.getUTCFullYear();
    return `${year}-${month}-${day}`;
  }

  static formatStringToDate(dateString: string) {
    if (typeof dateString === 'string') {
      const [day, month, year] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date;
    }
    return dateString;
  }

  static getCurrentDate() {
    return DateService.formatDateToDDMMYYYY(new Date());
  }

  static getDateDifference(
    date1: string,
    date2: string,
    unit: 'days' | 'hours' | 'minutes' | 'seconds' = 'days',
  ): number {
    const dateObj1 = new Date(date1);
    const dateObj2 = new Date(date2);

    if (isNaN(dateObj1.getTime()) || isNaN(dateObj2.getTime())) {
      throw new Error('Invalid date format');
    }

    const diffMilliseconds = Math.abs(dateObj2.getTime() - dateObj1.getTime());

    switch (unit) {
      case 'days':
        return diffMilliseconds / (1000 * 60 * 60 * 24);
      case 'hours':
        return diffMilliseconds / (1000 * 60 * 60);
      case 'minutes':
        return diffMilliseconds / (1000 * 60);
      case 'seconds':
        return diffMilliseconds / 1000;
      default:
        throw new Error('Invalid unit specified');
    }
  }
  static formatDateStringToYYYYMMDD(date: string): string | null {
    const parts = date.split('-');
    if (parts?.length) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  static formatDateValueToDDMMYYYY(date: Date): string {
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  static convertDateToString(
    date: string,
    format: string = 'dd/mm/yy hh:mm:ss',
  ) {
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());
    if (!date) {
      return '-';
    }

    switch (format) {
      case 'dd-mm-yy':
        return `${day}-${month}-${year}`;
      case 'dd/mm/yyyy':
        return `${day}/${month}/${year}`;
      default:
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }
  }

  static parseDate(date: string) {
    // Split into date and time parts
    const [datePart, timePart] = date.split(' ');

    // Break down the date (DD/MM/YYYY)
    const [day, month, year] = datePart.split('/').map(Number);

    // Break down the time (HH:mm:ss)
    const [hours, minutes, seconds] = timePart.split(':').map(Number);

    // Construct a Date object (month is 0-based in JS)

    return new Date(year, month - 1, day, hours, minutes, seconds);
  }
  static getDateQueryData(toDate: string | null, fromDate: string | null) {
    if (!toDate || !fromDate) {
      return;
    }
    const normalizedFromDate = fromDate ? new Date(fromDate) : undefined;
    normalizedFromDate?.setHours(0, 0, 0, 0);

    return {
      fromDate: normalizedFromDate
        ? formatDateToISO(normalizedFromDate)
        : undefined,
      toDate: toDate
        ? DateService.convertTimeStampToISO(toDate, true)
        : undefined,
    };
  }
}
